'use client';

import * as React from 'react';
import { Sheet, SheetClose, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from './sheet';
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from './dialog';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { Heading } from './heading';
import Image from 'next/image';

function useMediaQuery(query: string) {
  const [matches, setMatches] = React.useState(false);

  React.useEffect(() => {
    const mql = window.matchMedia(query);
    const onChange = () => setMatches(mql.matches);
    onChange();
    if (mql.addEventListener) {
      mql.addEventListener('change', onChange);
      return () => mql.removeEventListener('change', onChange);
    }
    mql.addListener(onChange);
    return () => mql.removeListener(onChange);
  }, [query]);

  return matches;
}

type ModalClassName = {
  header?: string;
  content?: string;
  overlayClassName?: string;
};

type ModalContextValue = {
  open: boolean;
  setOpen: (open: boolean) => void;
  close: () => void;
};

const ModalContext = React.createContext<ModalContextValue | null>(null);

export function useModal() {
  return React.useContext(ModalContext);
}

type ModalProps = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  trigger?: React.ReactNode;
  title?: string;
  description?: React.ReactNode;
  children: React.ReactNode;
  className?: ModalClassName;
  sheetSide?: React.ComponentProps<typeof SheetContent>['side'];
  modal?: boolean;
  showCustomClose?: boolean;
  showDefaultClose?: boolean;
  headerExtra?: React.ReactNode;
} & Omit<React.ComponentProps<typeof Dialog>, 'open' | 'onOpenChange'> &
  Omit<React.ComponentProps<typeof Sheet>, 'open' | 'onOpenChange'>;

export function Modal({
  open: openProp,
  onOpenChange,
  trigger,
  title,
  children,
  className,
  sheetSide = 'bottom',
  modal = false,
  showCustomClose,
  showDefaultClose = false,
  ...rest
}: ModalProps) {
  const isMobile = useMediaQuery('(max-width: 639px)');
  const isControlled = openProp !== undefined;

  const [uncontrolledOpen, setUncontrolledOpen] = React.useState(false);

  const open = isControlled ? openProp : uncontrolledOpen;

  const setOpen = React.useCallback(
    (next: boolean) => {
      if (!isControlled) setUncontrolledOpen(next);
      onOpenChange?.(next);
    },
    [isControlled, onOpenChange],
  );

  const ctx = React.useMemo<ModalContextValue>(
    () => ({
      open,
      setOpen,
      close: () => setOpen(false),
    }),
    [open, setOpen],
  );

  if (isMobile) {
    return (
      <ModalContext.Provider value={ctx}>
        <Sheet open={open} onOpenChange={setOpen} {...rest}>
          {trigger && <SheetTrigger asChild>{trigger}</SheetTrigger>}
          <SheetContent
            accessibleTitle={title}
            hasCustomTitle={!!title && !!showCustomClose}
            closeIcon={{ wrapper: showDefaultClose ? 'right-4 top-4' : 'hidden' }}
            aria-describedby={undefined}
            overlayClassName={cn('bg-black/25 backdrop-blur-none', className?.overlayClassName)}
            side={sheetSide}
            className={cn(
              'hide-scroll-bar max-h-[90dvh] w-full overflow-y-scroll rounded-b-none rounded-t-[24px] border-none px-4 py-12',
              className?.content,
            )}
          >
            {title && showCustomClose && (
              <SheetHeader
                className={cn(
                  'flex w-full flex-row items-center justify-between gap-4 sm:gap-6',
                  'ea-modal__sheet-header',
                  'ea-modal__header',
                  className?.header,
                )}
              >
                <SheetTitle>
                  <Heading tag="h4" className={{ title: 'uppercase' }} title={title} />
                </SheetTitle>
                <ModalClose>
                  <Image
                    src={'/images/icons/close-square.svg'}
                    alt={title}
                    width={32}
                    height={32}
                    className="object-contain"
                  />
                </ModalClose>
              </SheetHeader>
            )}
            {children}
          </SheetContent>
        </Sheet>
      </ModalContext.Provider>
    );
  }

  return (
    <ModalContext.Provider value={ctx}>
      <Dialog open={open} onOpenChange={setOpen} {...rest}>
        {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
        <DialogContent
          hideClose={!showDefaultClose}
          hasCustomTitle={!!title}
          aria-describedby={undefined}
          overlayClassName={cn('bg-black/30 backdrop-blur-none', className?.overlayClassName)}
          className={cn(
            'hide-scroll-bar max-h-[86dvh] w-full max-w-[792px] overflow-y-scroll rounded-[16px] border-none p-8 sm:max-w-[792px] md:p-12',
            className?.content,
          )}
          onInteractOutside={modal ? e => e.preventDefault() : undefined}
          onEscapeKeyDown={modal ? e => e.preventDefault() : undefined}
        >
          {title && !showCustomClose && <DialogTitle className="sr-only">{title}</DialogTitle>}
          {title && showCustomClose && (
            <DialogHeader
              className={cn(
                'flex w-full flex-row items-start justify-between gap-4 sm:gap-6',
                'ea-modal__dialog-header',
                'ea-modal__header',
                className?.header,
              )}
            >
              <DialogTitle>
                <Heading tag="h4" className={{ title: 'uppercase' }} title={title} />
              </DialogTitle>
              <ModalClose>
                <Image
                  src={'/images/icons/close-square.svg'}
                  alt={title}
                  width={32}
                  height={32}
                  className="object-contain"
                />
              </ModalClose>
            </DialogHeader>
          )}
          {children}
        </DialogContent>
      </Dialog>
    </ModalContext.Provider>
  );
}

type ModalCloseProps = React.ComponentProps<typeof DialogClose> &
  React.ComponentProps<typeof SheetClose>;

export function ModalClose(props: ModalCloseProps) {
  const isMobile = useMediaQuery('(max-width: 639px)');
  if (isMobile) return <SheetClose {...props} />;
  return <DialogClose {...props} />;
}
